import { Package, PackageState } from '../../../common/models';

const stages: Record<string, string> = {
    Preparation: 'Parsing document and generating previews',
    InterpretationResolution: 'Resolving document processing settings',
    InterpretationSourceIndex: 'Indexing OCR data for document processing',
    InterpretationSourceRules: 'Applying rules to OCR data',
    Interpretation: 'Processing document data',
    Publication: 'Publishing processing results',
    GeneratedIndex: 'Indexing processing results',
    GeneratedRules: 'Applying rules to processing results',
    SourceIndex: 'Indexing document data',
    SourceRules: 'Applying rules to document data',
    Complete: 'Complete'
};

const failures: Record<string, string> = {
    Preparation: 'Preparation failed (parsing/OCR and previews)',
    InterpretationResolution: 'Processing settings resolution failed',
    InterpretationSourceIndex: 'OCR data indexing failed',
    InterpretationSourceRules: 'OCR data rules failed',
    Interpretation: 'Interpretation failed (document processing)',
    Publication: 'Processing results publication failed',
    GeneratedIndex: 'Processing results indexing failed',
    GeneratedRules: 'Processing results rules failed',
    SourceIndex: 'Document data indexing failed',
    SourceRules: 'Document data rules failed'
};

export function packageProcessingPresentation(pkg: Package, sourceAvailable: boolean, startingPreparation = false) {
    const operation = pkg.currentOperation;
    const active = Boolean(operation?.active || startingPreparation || pkg.serverState === PackageState.Busy);
    const failed = pkg.serverState === PackageState.Broken && !active;
    const complete = pkg.serverState === PackageState.Ready && !active;
    const preparing = startingPreparation || (active && operation?.stage === 'Preparation');
    const stage = preparing ? stages.Preparation : (stages[operation?.stage ?? ''] ?? 'Processing document');
    const heading = failed
        ? (failures[operation?.stage ?? ''] ?? 'Document processing failed')
        : complete
          ? 'Processing complete — package ready'
          : `${pkg.error ? 'Recovering' : 'Processing'}: ${stage}`;
    const details = pkg.errorDetails?.trim() ? pkg.errorDetails : pkg.error;
    // Legacy responses sometimes repeat the same generic stage message. Keep
    // the original detailed cause intact, including long Python messages.
    const normalize = (text: string) =>
        text
            .trim()
            .toLowerCase()
            .replace(/[.\s]+$/g, '');
    const generic = [heading, `${operation?.stage} failed`, 'Interpretation failed', 'Preparation failed'];
    const lines = [heading];
    if (details && !generic.some(message => normalize(message) === normalize(details))) {
        lines.push(details);
    }
    if (
        operation &&
        pkg.publishedOutput &&
        pkg.publishedOutput.generation !== operation.id &&
        [
            'Preparation',
            'InterpretationResolution',
            'InterpretationSourceIndex',
            'InterpretationSourceRules',
            'Interpretation',
            'Publication'
        ].includes(operation.stage)
    ) {
        lines.push('Previously saved processing results are retained. They have not been replaced by the latest run.');
    }

    let sourceDescription: string;
    if (preparing) {
        sourceDescription = 'OCR and previews: being prepared.';
        if (sourceAvailable) {
            sourceDescription += ' Data from the previous successful preparation is retained.';
        }
    } else if (!sourceAvailable) {
        // Missing preparation metadata does not mean that legacy OCR or previews are absent.
        sourceDescription =
            'OCR and previews: reuse for reprocessing is not verified. Reparse the document before reprocessing.';
    } else if (pkg.preparedSourceMatchesSettings !== true) {
        sourceDescription =
            pkg.preparedSourceMatchesSettings === false
                ? 'OCR and previews: previous data retained. It does not match the saved preparation settings. A successful reparse is required.'
                : 'OCR and previews: retained data is available, but its preparation settings cannot be verified. A successful reparse is required.';
    } else {
        sourceDescription =
            'OCR and previews: available. Reprocess the document to rerun subsequent steps without repeating parsing/OCR.';
        if (failed && operation?.stage === 'Preparation') {
            sourceDescription +=
                ' The latest preparation failed; this data is from a previous successful preparation with matching settings.';
        }
    }
    return {
        active,
        failed,
        complete,
        preparing,
        processingDescription: lines.join('\n\n'),
        sourceDescription,
        sourceClass:
            preparing || !sourceAvailable
                ? 'pending'
                : pkg.preparedSourceMatchesSettings === true
                  ? 'complete'
                  : 'unavailable'
    };
}
